// VodPlayerPool.js
'use strict';
import PriorityPool from '../resource/PriorityPool.js';
import { player as playerDebug } from '../resource/debug.js';
import { getAdapterByEngine } from '../resource/playerConfig.js';
import { PlayerEngine } from '../selector/getCurrentEngine.js';

const poolDebug = playerDebug.extend('log:VodPlayerPool');
const poolError = playerDebug.extend('error:VodPlayerPool');

const DEFAULT_VOD_PLAYER_POOL_SIZE_MAX = 5;
let VOD_PLAYER_POOL_SIZE_MAX =
  Number(process.env.VOD_PLAYER_POOL_SIZE_DESKTOP_MAX) ||
  DEFAULT_VOD_PLAYER_POOL_SIZE_MAX;

const DESTROY_TIMEOUT_MS = 3000;

export const setVodPlayerPoolSizeMax = ({ size }) => {
  if (typeof size !== 'number' || size <= 0) return;
  VOD_PLAYER_POOL_SIZE_MAX = size;
  poolDebug('setVodPlayerPoolSizeMax', { size });
  _playerPoolInstance?.setMaxSize(size);
};

class VodPlayerPool {
  constructor(options = {}) {
    if (!options.AdapterClass) {
      throw new Error('VodPlayerPool requires AdapterClass option');
    }

    this._AdapterClass = options.AdapterClass;
    this._adapterConfig = options.adapterConfig || {};
    this._isMuted = true;
    /** @type {Map<string, () => void>} */
    this._signalCleanups = new Map();

    this._pool = new PriorityPool({
      maxSize: options.maxSize || VOD_PLAYER_POOL_SIZE_MAX,
      releaseTimeoutMs: DESTROY_TIMEOUT_MS,
      onAllocate: (id, poolResource) => this._onAllocate(id, poolResource),
      onRelease: (id, poolResource) => this._onRelease(id, poolResource),
      debugNameSpace: 'VodPlayerPool',
    });
  }

  async ensureReady() {
    if (this._AdapterClass.ensureReady) {
      await this._AdapterClass.ensureReady();
    }
  }

  async acquire(
    playerId,
    { source, videoElement, priority, onReady, onPreempted, signal }
  ) {
    if (signal?.aborted) {
      const error = new Error('VodPlayerPool acquire aborted');
      error.name = 'AbortError';
      throw error;
    }

    this._signalCleanups.get(playerId)?.();
    this._signalCleanups.delete(playerId);

    if (signal) {
      const onAbort = () => this.cancelAcquire(playerId);
      signal.addEventListener('abort', onAbort, { once: true });
      this._signalCleanups.set(playerId, () =>
        signal.removeEventListener('abort', onAbort)
      );
    }

    try {
      await this._pool.acquire(playerId, {
        priority,
        onPreempted,
        source,
        videoElement,
        onReady,
      });
    } catch (error) {
      poolError(
        `acquire ERROR | ${playerId}: ${error?.message || String(error)}`
      );
      throw error;
    }
  }

  cancelAcquire(playerId) {
    this._signalCleanups.get(playerId)?.();
    this._signalCleanups.delete(playerId);
    return this._pool.release(playerId);
  }

  release(playerId) {
    this._signalCleanups.get(playerId)?.();
    this._signalCleanups.delete(playerId);
    return this._pool.release(playerId);
  }

  updatePriority(playerId, priority) {
    return this._pool.updatePriority(playerId, priority);
  }

  getInternalPlayer(playerId) {
    return this._pool.get(playerId)?.adapter?.getInternalPlayer() || null;
  }

  getAdapter(playerId) {
    return this._pool.get(playerId)?.adapter || null;
  }

  getAdapterContainer(playerId) {
    return this._pool.get(playerId)?.adapter?.getContainer?.() || null;
  }

  setMaxSize(size) {
    this._pool.setMaxSize(size);
  }

  getStatus() {
    const status = this._pool.getPoolStatus();
    return {
      poolKey: 'vod',
      maxSize: status.maxSize,
      allocated: status.allocated,
      pending: status.pending,
      releasing: status.releasing,
      availableCapacity: Math.max(status.maxSize - status.allocated, 0),
    };
  }

  syncMutedState(isMuted) {
    this._isMuted = isMuted;
    for (const adapter of this._allocatedAdapters()) {
      try {
        isMuted ? adapter.mute() : adapter.unmute();
      } catch (error) {
        poolError(`syncMutedState: skipped: ${error.message}`);
      }
    }
  }

  *_allocatedAdapters() {
    const adapters = [];
    this._pool.forEachAllocated(poolResource => {
      if (poolResource.adapter) adapters.push(poolResource.adapter);
    });
    yield* adapters;
  }

  async _onAllocate(playerId, poolResource) {
    const { videoElement } = poolResource;
    if (!videoElement) {
      throw new Error('VodPlayerPool: video element is required');
    }

    const adapter = new this._AdapterClass({ ...this._adapterConfig });

    try {
      await adapter.initialize(videoElement);
      const loadedSource = poolResource.source;
      await adapter.load(loadedSource);
      this._isMuted ? adapter.mute?.() : adapter.unmute?.();

      poolResource.adapter = adapter;
      poolResource.loadedSource = loadedSource;
      poolResource.onReady?.(loadedSource);
    } catch (error) {
      try {
        adapter.pause?.();
        await adapter.destroy();
      } catch (destroyError) {
        poolError(
          `allocate:destroyError | ${playerId}: ${destroyError?.message || String(destroyError)}`
        );
      }
      throw error;
    }
  }

  async _onRelease(playerId, poolResource) {
    const { adapter } = poolResource;
    poolResource.adapter = null;
    if (!adapter) return;

    try {
      adapter.mute?.();
      adapter.pause?.();
      await adapter.unload();
    } catch (error) {
      poolError(
        `release:unloadError | ${playerId}: ${error?.message || String(error)}`
      );
    }

    try {
      await adapter.destroy();
    } catch (error) {
      poolError(
        `release:destroyError | ${playerId}: ${error?.message || String(error)}`
      );
    }
  }
}

let _playerPoolInstance = null;

export const getPlayerPool = ({
  engine = PlayerEngine.PRESTOPLAY,
  ...options
} = {}) => {
  if (_playerPoolInstance) return _playerPoolInstance;

  if (!options.AdapterClass) {
    const adapterEntry = getAdapterByEngine(engine);
    if (!adapterEntry) return null;
    options.AdapterClass = adapterEntry.AdapterClass;
    options.adapterConfig = adapterEntry.adapterConfig;
  }

  _playerPoolInstance = new VodPlayerPool(options);

  if (typeof window !== 'undefined') {
    window.__playerPool__ = _playerPoolInstance;
  }
  return _playerPoolInstance;
};

export { VodPlayerPool, DESTROY_TIMEOUT_MS };
